// src/modules/units/tyres/views/retread-batch/retreadbatchdetail.js
//
// Orchestrator for one retread batch: refetch the authoritative record by id,
// resolve its supplier name (and a yard, when every tyre on it shares one), and
// render via the pure view. Read-only — the actions that change a batch (dispatch,
// receive, cancel, and acting on the recovery banner) arrive with the write forms.
// Printing a consignment sheet (Print) is available to everyone, as is emailing it
// once the batch has been dispatched (Email); both are handled by retreadbatchprint.js.
//
// The seed row only needs a retreadBatchId; everything shown comes from the fetch,
// so opening a batch from a stale list row is safe.

import { fetchRetreadBatch, removeRetreadBatchLine, rollbackRetreadBatch } from '../retread-batch.api.js';
import { fetchSupplier } from '../suppliers.api.js';
import { detailHTML } from './retreadbatchdetail.view.js';
import { printRetreadBatch, emailRetreadBatch } from './retreadbatchprint.js';
import { canManageBatches } from '../../shared/retread-batch-access.js';
import { icon } from '../../shared/tyre-icons.js';
import { esc } from '../../shared/tyre-format.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

function stateHTML(kind, message, { retry = false } = {}) {
  const retryBtn = retry ? ` <button type="button" class="tm-btn-link" data-rb-retry>Retry</button>` : '';
  return `
    <div class="tm-rb-state">
      <span class="tm-state">
        <span class="tm-state-icon">${icon(kind === 'error' ? 'reports' : 'retread')}</span>
        <span class="tm-state-msg">${esc(message)}${retryBtn}</span>
      </span>
    </div>`;
}

// Only claim a yard when every tyre on the batch agrees on one. Reads the likely
// line fields defensively; if lines don't carry a yard, returns '' and the fact
// is simply omitted rather than guessed.
function uniformYard(lines) {
  if (!Array.isArray(lines) || !lines.length) return '';
  let label = '';
  for (const l of lines) {
    const name = l?.yardName ?? l?.yard ?? null;
    const id = l?.yardId ?? null;
    const key = (name ?? id);
    if (key === null || key === undefined || key === '') return ''; // a line with no yard → can't claim uniform
    if (!label) label = String(name ?? id);
    else if (String(name ?? id) !== label) return '';               // disagreement → omit
  }
  return label;
}

/**
 * @param {HTMLElement} viewEl controller view slot (emptied first)
 * @param {object} ctx
 * @param {{retreadBatchId:string}} ctx.batch seed row (needs retreadBatchId)
 * @param {(batch:object)=>void} [ctx.onAddTyres] the Add tyres button (admins, draft only)
 * @param {(batch:object)=>void} [ctx.onEdit] the Edit details button (admins, draft only)
 * @param {(batch:object, supplierName:string)=>void} [ctx.onDispatch] the Dispatch button (admins, draft with tyres)
 * @param {(batch:object)=>void} [ctx.onReceive] the Receive button (admins, open/partially-returned)
 * @param {(batch:object)=>void} [ctx.onCancel] the Cancel batch button (admins, draft or open)
 * @returns {() => void} teardown
 */
export function renderRetreadBatchDetail(viewEl, ctx = {}) {
  let disposed = false;
  const batchId = ctx.batch?.retreadBatchId;
  const canManage = canManageBatches();
  let currentBatch = null;
  let currentSupplierName = '';
  let currentSupplierEmail = '';

  const root = el(`<div class="tm-rb-detail-root">${stateHTML('loading', 'Loading batch…')}</div>`);
  viewEl.replaceChildren(root);

  function setContent(html) { root.innerHTML = html; }

  async function load() {
    if (!batchId) { setContent(stateHTML('error', 'No batch to show.')); return; }
    setContent(stateHTML('loading', 'Loading batch…'));

    const res = await fetchRetreadBatch(batchId);
    if (disposed) return;

    if (!res.success) {
      setContent(stateHTML('error', res.message || 'Could not load this batch.', { retry: true }));
      return;
    }

    // The batch header lives on res.batch; its lines on res.lines. Fold lines onto
    // the batch so the pure view gets one object.
    const batch = { ...(res.batch || {}), lines: res.lines || res.batch?.lines || [] };

    // Resolve the supplier name (light single fetch; best-effort).
    let supplierName = '';
    let supplierEmail = '';
    if (batch.supplierId !== undefined && batch.supplierId !== null) {
      const sup = await fetchSupplier(batch.supplierId);
      if (disposed) return;
      if (sup.success) {
        supplierName = sup.data?.supplierName || '';
        supplierEmail = sup.data?.eMailAddress || '';
      }
    }

    const yardLabel = uniformYard(batch.lines);
    currentBatch = batch;
    currentSupplierName = supplierName;
    currentSupplierEmail = supplierEmail;
    setContent(detailHTML(batch, { supplierName, yardLabel, canManage }));
  }

  function setDetailMsg(text, kind) {
    const box = root.querySelector('[data-rb-detail-msg]');
    if (!box) return;
    box.textContent = text || '';
    box.hidden = !text;
    box.className = `tm-form-msg tm-rb-detail-msg${kind ? ` tm-form-msg-${kind}` : ''}`;
  }

  // Remove one untouched draft line. On success the API client emits
  // RETREAD_BATCH_CHANGED, so the subscription below refetches and the row goes.
  async function removeLine(lineId, btn) {
    if (!lineId) return;
    setDetailMsg('');
    if (btn) btn.disabled = true;
    const res = await removeRetreadBatchLine(batchId, lineId, { reason: 'remove-line' });
    if (disposed) return;
    if (!res.success) {
      if (btn) btn.disabled = false;
      if (res.status === 403) setDetailMsg('You need administrator access to remove a tyre.', 'error');
      else if (res.locked) setDetailMsg('This batch is busy with another operation. Try again in a moment.', 'error');
      else setDetailMsg(res.message || 'Could not remove the tyre.', 'error');
    }
  }

  // Restore a failed/wedged operation's before-state (administrators). On success
  // the events refetch; failures surface in the detail message area.
  async function rollback(btn) {
    setDetailMsg('');
    if (btn) btn.disabled = true;
    const res = await rollbackRetreadBatch(batchId, { reason: 'rollback' });
    if (disposed) return;
    if (!res.success) {
      if (btn) btn.disabled = false;
      if (res.status === 403) setDetailMsg('You need administrator access to roll back this batch.', 'error');
      else if (res.noRollback) setDetailMsg('There’s no rollback available for this batch — contact support.', 'error');
      else setDetailMsg(res.message || 'Could not roll back the batch.', 'error');
    }
  }

  // Build and print a consignment sheet from the batch currently in view. The
  // gather (customer name + a full tyre record per line) is best-effort and lives
  // in retreadbatchprint.js; here we only reflect progress on the button and
  // surface a failure to produce the document at all.
  async function printCurrent(btn) {
    if (!currentBatch) return;
    setDetailMsg('');
    const labelEl = btn ? btn.querySelector('[data-rb-print-label]') : null;
    const prevLabel = labelEl ? labelEl.textContent : '';
    if (btn) btn.disabled = true;
    if (labelEl) labelEl.textContent = 'Preparing…';

    const res = await printRetreadBatch(currentBatch, { supplierName: currentSupplierName });
    if (disposed) return;

    if (btn) btn.disabled = false;
    if (labelEl) labelEl.textContent = prevLabel || 'Print';
    if (!res || !res.success) setDetailMsg((res && res.message) || 'Could not prepare the print.', 'error');
  }

  // Email the sheet: opens the user's mail client with the sheet in the body and a
  // formatted copy on the clipboard (see emailRetreadBatch). The call must stay the
  // first async step after the click so the clipboard write keeps the user gesture.
  async function emailCurrent(btn) {
    if (!currentBatch) return;
    setDetailMsg('');
    const labelEl = btn ? btn.querySelector('[data-rb-email-label]') : null;
    const prevLabel = labelEl ? labelEl.textContent : '';
    if (btn) btn.disabled = true;
    if (labelEl) labelEl.textContent = 'Preparing…';

    const res = await emailRetreadBatch(currentBatch, {
      supplierName: currentSupplierName,
      supplierEmail: currentSupplierEmail
    });
    if (disposed) return;

    if (btn) btn.disabled = false;
    if (labelEl) labelEl.textContent = prevLabel || 'Email';
    if (!res || !res.success) { setDetailMsg((res && res.message) || 'Could not prepare the email.', 'error'); return; }

    const bits = [];
    bits.push(res.to ? `Email opened in your mail app, addressed to ${res.to}.` : 'Email opened in your mail app — add the recipient.');
    if (res.copied) {
      bits.push(res.truncated
        ? 'The tyre list was too long for the message body — it’s on your clipboard: paste it in (Ctrl+V).'
        : 'A formatted copy of the sheet is on your clipboard if you’d rather paste that in (Ctrl+V).');
    } else if (res.truncated) {
      bits.push('The tyre list was too long for the message body — use Print to attach the sheet.');
    }
    setDetailMsg(bits.join(' '), 'ok');
  }

  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-rb-retry]')) { load(); return; }
    if (e.target.closest('[data-rb-recover-refresh]')) { load(); return; }
    if (e.target.closest('[data-rb-recover-rollback]')) { rollback(e.target.closest('[data-rb-recover-rollback]')); return; }
    if (e.target.closest('[data-rb-print]')) { printCurrent(e.target.closest('[data-rb-print]')); return; }
    if (e.target.closest('[data-rb-email]')) { emailCurrent(e.target.closest('[data-rb-email]')); return; }
    if (e.target.closest('[data-rb-add-tyres]')) {
      if (typeof ctx.onAddTyres === 'function' && currentBatch) ctx.onAddTyres(currentBatch);
      return;
    }
    if (e.target.closest('[data-rb-edit]')) {
      if (typeof ctx.onEdit === 'function' && currentBatch) ctx.onEdit(currentBatch);
      return;
    }
    if (e.target.closest('[data-rb-dispatch]')) {
      if (typeof ctx.onDispatch === 'function' && currentBatch) ctx.onDispatch(currentBatch, currentSupplierName);
      return;
    }
    if (e.target.closest('[data-rb-receive]')) {
      if (typeof ctx.onReceive === 'function' && currentBatch) ctx.onReceive(currentBatch);
      return;
    }
    if (e.target.closest('[data-rb-cancel]')) {
      if (typeof ctx.onCancel === 'function' && currentBatch) ctx.onCancel(currentBatch);
      return;
    }
    const rm = e.target.closest('[data-rb-remove-line]');
    if (rm) { removeLine(rm.dataset.lineId, rm); return; }
  });

  load();
  // A batch change, or a tyre change (receiving moves tyres and updates this
  // batch), refetches.
  const offBatch = onTyreEvent(TYRE_EVENTS.RETREAD_BATCH_CHANGED, load);
  const offTyre = onTyreEvent(TYRE_EVENTS.CHANGED, load);

  return () => { disposed = true; offBatch(); offTyre(); };
}
